import { useCallback } from 'react'
import { useSetViewSheetLayer } from './atoms'
import { SIDEPANEL_LAYER_Z } from './constants'

/**
 * The layer a View's bottom sheet mounts into — today the Action View's chat
 * drawer (HIG-5977).
 *
 * A sheet can't live inside the View it covers. The router gives each entry a
 * transformed swipe layer, which is a stacking context whatever z-index its
 * descendants ask for, so a sheet declared inside a View is pinned below the
 * floating bar: the busy pill and the action's controls floated on top of the
 * sheet instead of being covered by it. The sheet gets its own layer on the
 * view's rect instead, above that bar and below the chat input, and Views
 * portal into it.
 *
 * The cost is that a sheet no longer rides the view swap: navigating away with
 * one open drops it rather than dissolving it along with the view behind it.
 *
 * Inert and empty until something portals in — `pointer-events-none` here, and
 * the sheet's own scrim and popup opt back in.
 *
 * In a popout there is no card to merge the sheet's bottom into, so the layer
 * runs past the view's rect and under the composer; the composer's higher
 * z-index covers the overshoot and the sheet reads as rising out of it.
 */
export function ViewSheetLayer() {
  const setLayer = useSetViewSheetLayer()

  const ref = useCallback((element: HTMLDivElement | null) => setLayer(element), [setLayer])

  return (
    <div
      ref={ref}
      className="app:-bottom-6 app:inset-x-3 pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: SIDEPANEL_LAYER_Z.viewSheet }}
    />
  )
}
